import type { RuntimeState } from '../domain/runtime.ts';
import { RUNTIME_SCHEMA_VERSION, migrateRuntimeRecord, validateRuntime } from '../domain/runtime.ts';
import { ENCOUNTER_SCHEMA_VERSION } from '../domain/encounter.ts';

/**
 * Whether a persisted runtime record is one THIS BUILD may open.
 *
 * ONE authority, used by the repository on load and by the checkpoint
 * committer when it reads a journal's runtime leg. Both face the same question
 * - "can I safely understand this?" - and answering it twice would eventually
 * mean answering it two different ways.
 *
 * THE DISTINCTION THIS EXISTS TO DRAW. "There is no runtime record" and "there
 * is a runtime record I cannot read" are completely different facts, and the
 * repository used to collapse them into one: both produced a fresh blank
 * runtime. The second is dangerous, because the blank record then looks
 * authoritative and the very next save writes it over persisted state - Effects,
 * cooldowns, elapsed time, an open fight - that this build merely failed to
 * parse. Absence is answered by starting fresh. Unreadability is answered by
 * refusing, and by touching nothing.
 */
export type RuntimeLoadRefusalKind =
  /** The record itself was written by a newer build of the application. */
  | 'newer-version'
  /** The record is readable, but the encounter inside it is from a newer build. */
  | 'newer-encounter'
  /** Same version, but the record does not satisfy this build's validators. */
  | 'invalid-record'
  /** The record is well-formed but describes a different crawler. */
  | 'identity';

export class RuntimeLoadRefusal extends Error {
  readonly kind: RuntimeLoadRefusalKind;

  constructor(kind: RuntimeLoadRefusalKind, message: string) {
    super(message);
    this.name = 'RuntimeLoadRefusal';
    this.kind = kind;
  }
}

export type RuntimeInspection =
  | { ok: true; runtime: RuntimeState }
  | { ok: false; kind: RuntimeLoadRefusalKind; message: string };

/**
 * Inspects a raw stored runtime record on behalf of `characterId`.
 *
 * PURE. It reads and decides; it never writes, never deletes and never repairs.
 * The whole safety property is that a build which did not understand the data
 * did not alter the data, and that is easiest to guarantee by giving this no
 * way to alter anything.
 *
 * The order of the checks matters. The version is read from the RAW record,
 * before `migrateRuntimeRecord` touches it, because running a migration chain
 * over a shape from the future is guesswork dressed as compatibility.
 */
export function inspectRuntimeRecord(raw: unknown, characterId: string): RuntimeInspection {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return {
      ok: false,
      kind: 'invalid-record',
      message:
        `This crawler has saved runtime state that cannot be safely read. ` +
        `Nothing has been changed.`,
    };
  }

  const record = raw as Record<string, unknown>;

  // FIRST, and deliberately before any migration runs.
  const version = record['schemaVersion'];
  if (typeof version === 'number' && version > RUNTIME_SCHEMA_VERSION) {
    return {
      ok: false,
      kind: 'newer-version',
      message:
        `This crawler's runtime state was written by a newer version of the application ` +
        `(format ${version}; this build understands ${RUNTIME_SCHEMA_VERSION}). ` +
        `Open the crawler with that version. Nothing has been changed.`,
    };
  }

  const migrated = migrateRuntimeRecord(record);
  if (typeof migrated !== 'object' || migrated === null) {
    return {
      ok: false,
      kind: 'invalid-record',
      message:
        `This crawler has saved runtime state that cannot be safely read. ` +
        `Nothing has been changed.`,
    };
  }

  // The encounter nests inside, and carries its own version. A.0 made this
  // detectable; refusing rather than blanking is what makes it safe.
  const nested = (migrated as Record<string, unknown>)['encounter'];
  if (typeof nested === 'object' && nested !== null && !Array.isArray(nested)) {
    const nestedVersion = (nested as Record<string, unknown>)['schemaVersion'];
    if (typeof nestedVersion === 'number' && nestedVersion > ENCOUNTER_SCHEMA_VERSION) {
      return {
        ok: false,
        kind: 'newer-encounter',
        message:
          `This crawler is in an encounter recorded by a newer version of the application ` +
          `(format ${nestedVersion}; this build understands ${ENCOUNTER_SCHEMA_VERSION}). ` +
          `Open the crawler with that version. Nothing has been changed.`,
      };
    }
  }

  // IDENTITY BEFORE VALIDATION, so a record about the wrong crawler is reported
  // as the wrong crawler rather than as generic corruption. A caller that has
  // to read the prose to tell those apart will eventually read it wrong.
  const owner = (migrated as Record<string, unknown>)['characterId'];
  const nestedOwner =
    typeof nested === 'object' && nested !== null
      ? (nested as Record<string, unknown>)['characterId']
      : undefined;
  // A.2: `identity` means the record NAMES SOMEONE ELSE. A record with no
  // usable id is malformed instead, and falls through to the validator - which
  // is the thing that knows `characterId` is required. Aligned with the
  // Character and Session legs so the block matrix has one rule, not three.
  const namesAnother =
    (typeof owner === 'string' && owner !== characterId) ||
    (typeof nestedOwner === 'string' && nestedOwner !== characterId);
  if (namesAnother) {
    return {
      ok: false,
      kind: 'identity',
      message:
        `The saved runtime state does not belong to this crawler ` +
        `(it names "${String(nestedOwner !== characterId && nestedOwner !== undefined ? nestedOwner : owner)}"). ` +
        `Nothing has been changed.`,
    };
  }

  const check = validateRuntime(migrated);
  if (!check.valid) {
    return {
      ok: false,
      kind: 'invalid-record',
      message:
        `This crawler has saved runtime state that cannot be safely read. ` +
        `Nothing has been changed. (${check.errors.join(' ')})`,
    };
  }

  return { ok: true, runtime: migrated as RuntimeState };
}
